import SEO from '@/components/common/SEO';
import { Breadcrumb } from '@/components/common/Breadcrumb';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  Briefcase,
  Building,
  Heart,
  Scale,
  Factory,
  BookOpen,
  ChevronRight,
  Home,
  Landmark,
  GraduationCap,
  FlaskConical,
  Video,
  Presentation,
} from 'lucide-react';

// Industry icon mapping
const industryIconMap = {
  'Cross Industry Functions': Briefcase,
  'Healthcare and Hospital': Heart,
  'Banking Financial Services and Insurance': Landmark,
  'Manufacturing': Factory,
  'Construction': Building,
  'Legal and Law': Scale,
  'Real Estate': Home,
  'Other Trainings': BookOpen,
  'Generic Webinars': GraduationCap,
  'Life Sciences': FlaskConical,
  'default': Briefcase
};

const getIndustryIcon = (industryName) => {
  return industryIconMap[industryName] || industryIconMap.default;
};

export default function IndustriesPage() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMainIndustries();
  }, []);

  const fetchMainIndustries = async () => {
    try {
      // Use the /main endpoint which returns only parent industries with sub-industries and counts
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/industries/main`);
      const data = await response.json();
      setIndustries(data || []);
    } catch (error) {
      console.error('Error fetching industries:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="industries-page">
      <SEO
        title="Industries We Serve — Compliance Training by Sector"
        description="Specialized GRC webinars and certifications tailored to healthcare, banking, HR, finance, life sciences, IT security and 45+ regulated industries."
        path="/industries"
      />
      {/* Header — compact, grid canvas */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-8">
        <div className="container-custom">
          <Breadcrumb />
          <div className="flex flex-col sm:flex-row sm:items-end gap-x-4 gap-y-2">
            <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-[-0.03em] leading-none">
              <span className="text-zinc-950">Industries </span>
              <span className="text-slate-400">we serve.</span>
            </h1>
            <p className="text-zinc-500 text-sm md:text-base sm:pb-0.5">
              Industry-specific governance, risk &amp; compliance training tailored to your regulatory landscape.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid - Only Main Industries */}
      <section className="py-10">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-12 w-12 bg-zinc-200 rounded-lg mb-4" />
                    <div className="h-6 bg-zinc-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-zinc-200 rounded w-full mb-4" />
                    <div className="h-4 bg-zinc-200 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {industries.map((industry, idx) => {
                const IconComponent = getIndustryIcon(industry.name);
                const totalContent = (industry.webinar_count || 0) + (industry.seminar_count || 0);
                
                return (
                  <Card
                    key={industry.id}
                    className="group overflow-hidden bg-white border border-zinc-200 hover:border-zinc-900 transition-colors duration-200 animate-fade-in opacity-0 rounded-xl"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                    data-testid={`industry-card-${industry.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Link 
                          to={`/industries/${industry.slug || industry.id}`} 
                          className="w-14 h-14 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center flex-shrink-0 group-hover:bg-zinc-900 group-hover:border-zinc-900 transition-colors duration-200"
                        >
                          <IconComponent className="w-7 h-7 text-zinc-700 group-hover:text-white transition-colors" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/industries/${industry.slug || industry.id}`}>
                            <h3 className="font-heading text-xl font-semibold text-zinc-900 mb-2 group-hover:text-zinc-700 transition-colors cursor-pointer">
                              {industry.name}
                            </h3>
                          </Link>
                          <p className="text-zinc-600 text-sm mb-4 line-clamp-2">
                            {industry.description || 'Specialized compliance training for industry professionals.'}
                          </p>
                          
                          {/* Webinar/Seminar Counts */}
                          <div className="flex items-center gap-4 mb-4">
                            <div className="flex items-center gap-1.5 text-sm">
                              <Video className="w-4 h-4 text-zinc-400" />
                              <span className="text-zinc-900 font-medium">{industry.webinar_count || 0}</span>
                              <span className="text-zinc-500">Webinars</span>
                            </div>
                            {industry.seminar_count > 0 && (
                              <div className="flex items-center gap-1.5 text-sm">
                                <Presentation className="w-4 h-4 text-zinc-400" />
                                <span className="text-zinc-900 font-medium">{industry.seminar_count}</span>
                                <span className="text-zinc-500">Seminars</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Sub-industries */}
                          {industry.sub_industries?.length > 0 && (
                            <div className="mb-4">
                              <p className="mono-label text-[10px] text-zinc-500 mb-2">
                                Topics Covered
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {industry.sub_industries.slice(0, 5).map((sub) => (
                                  <Link
                                    key={sub.id}
                                    to={`/industries/${sub.slug || sub.id}`}
                                  >
                                    <Badge
                                      variant="secondary"
                                      className="bg-zinc-100 text-zinc-600 text-xs hover:bg-zinc-900 hover:text-white transition-colors cursor-pointer border border-zinc-200"
                                    >
                                      {sub.name}
                                    </Badge>
                                  </Link>
                                ))}
                                {industry.sub_industries.length > 5 && (
                                  <Badge variant="secondary" className="bg-zinc-100 text-zinc-600 text-xs border border-zinc-200">
                                    +{industry.sub_industries.length - 5} more
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}

                          <Link
                            to={`/industries/${industry.slug || industry.id}`}
                            className="inline-flex items-center text-zinc-900 hover:text-emerald-600 text-sm font-medium transition-colors"
                          >
                            Explore Training
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white border-t border-zinc-200">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl font-bold text-zinc-900 mb-4">
              Don't See Your Industry?
            </h2>
            <p className="text-zinc-600 mb-8">
              Contact us to discuss custom training solutions tailored to your 
              specific industry requirements.
            </p>
            <Link to="/contact">
              <Button className="bg-zinc-900 hover:bg-zinc-800 text-white">
                Contact Us
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
